import { SingletonRouter, withRouter } from 'next/router'
import React, { FC, useCallback, useEffect } from 'react'

import { compose } from '@reduxjs/toolkit'
import { CardNumberElement } from '@stripe/react-stripe-js'
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { reduxForm, InjectedFormProps, getFormValues } from 'redux-form'

import { Box } from '@material-ui/core'

import AppleGooglePayButton from '~/components/buttons/AppleGooglePayButton/AppleGooglePayButton'
import StripeForm from '~/components/forms/StripeForm'
import CheckboxSection from '~/components/shared/CheckboxSection'
import CheckoutList, { CheckoutLine } from '~/components/shared/CheckoutList'
import { WizardStepProps } from '~/components/shared/Wizard/types'
import useWizard from '~/components/shared/Wizard/useWizard'
import WizardStepWrapper from '~/components/shared/Wizard/WizardStepWrapper'
import withStripe from '~/containers/withStripe'

import usePaymentStripe from '~/hooks/usePaymentStripe'
import EmpriseGroup from '~/services/EmpriseGroup'
import {
  getPackage,
  getFeatures,
  getTotalPackage,
  getPackageFeatures
} from '~/state/modules/package'
import { getUserPhone, hasRole, updateUser } from '~/state/modules/user'
import ErrorHandler from '~/utils/errorHandler'
import {
  capitalizeFirstLetter,
  formatCurrency,
  getFormattedListingStepData
} from '~/utils/functions'
import { getStripeClientSecret, PaymentProps } from '~/utils/payment'

import { Listing, ListingDTO } from '~/models/Listing/interface'
import { Profile } from '~/models/Profile'

import { useListingCheckoutStepStyles } from './ListingInformation.styles'

const CheckoutStep: FC<
  WizardStepProps & InjectedFormProps & SingletonRouter
> = ({ form, router, handleSubmit, onNext, onBack, submitting, change }) => {
  const s = useListingCheckoutStepStyles()
  const currentPackage = useSelector(getPackage)
  const total = useSelector(getTotalPackage)
  const features = useSelector(getPackageFeatures)
  const featureIds = useSelector(getFeatures)
  const values = useSelector<
    any,
    Partial<Profile & { images: Listing['images'] }>
  >(getFormValues(form))
  const userPhone = useSelector(getUserPhone)
  const isGuest = useSelector(hasRole('guest'))
  const { store, setStore, state } = useWizard()
  const dispatch = useDispatch()

  useEffect(() => {
    change('update_me_with_latest_news', true)

    const saveListing = async () => {
      setStore((state) => ({ ...state, loading: true }))

      const listingStepDto = getFormattedListingStepData(
        state.step,
        state.stepTitles
      )

      const { images, ...listingValues } = values

      try {
        if (store?.subject_id) {
          await EmpriseGroup.updateListing(store?.subject_id, {
            ...listingValues,
            creation_completed: true,
            ...listingStepDto
          })

          toast.success('Listing has been updated')
        } else {
          const { data } = await EmpriseGroup.createListing({
            ...values,
            creation_completed: true,
            ...listingStepDto
          })

          toast.success('Listing has been saved to draft')

          setStore({ subject_id: data.id })

          if (!userPhone && !isGuest) {
            // eslint-disable-next-line @typescript-eslint/await-thenable
            await dispatch(updateUser({ phone: values.phone }))
          }
        }
      } catch (error) {
        throw new ErrorHandler(error)
      } finally {
        setStore((state) => {
          return {
            ...state,
            loading: false,
            completed: true,
            ...(isGuest && { disableNavigation: true })
          }
        })
      }
    }

    saveListing()
  }, [])

  const paymentProps: PaymentProps = {
    subjectType: 'Listing',
    subjectId: store?.subject_id,
    packageId: currentPackage.id,
    featureIds,
    updateMeWithLatestNews: values?.update_me_with_latest_news
  }

  const onSubmitSuccess = () => {
    toast.success('Your ad was created')
    router.push('/congratulation')

    window.scrollTo({ top: 0 })
  }

  const { handlePaymentSubmit, loaded, processing } = usePaymentStripe({
    Element: CardNumberElement,
    getClientSecret: () => getStripeClientSecret(paymentProps),
    onSubmitSuccess
  })

  const submit = useCallback(async () => {
    await handlePaymentSubmit()
  }, [handlePaymentSubmit])

  const isDisable = !loaded || store?.loading || processing
  const isSubmitting = submitting || processing
  const isDisableNext = store?.paymentError || !values.privacy
  const packageTitle = `Package "${capitalizeFirstLetter(
    currentPackage?.name
  )}"`
  const isDisabledPayButton = !values?.privacy
  // const isPaypalLoading = !loaded || processing || submitting

  return (
    <WizardStepWrapper
      title="Checkout"
      onNext={onNext}
      onBack={onBack}
      handleSubmit={handleSubmit(submit)}
      nextText={`Pay ${formatCurrency(total)}`}
      submitting={isSubmitting}
      disabled={isDisable}
      disabledBack={isGuest}
      disabledNext={isDisableNext}
    >
      <CheckoutList title="You Choose" divider>
        <CheckoutLine
          name={packageTitle}
          price={currentPackage?.price?.cents}
          nameSize={18}
          priceSize={18}
          className={s.price}
        />
      </CheckoutList>

      {!!features?.length && (
        <CheckoutList title="Additional" divider>
          {features?.map(({ name, price, id }) => (
            <CheckoutLine
              key={id}
              name={name}
              price={price.cents}
              nameSize={18}
              priceSize={18}
            />
          ))}
        </CheckoutList>
      )}

      <CheckoutLine name="Total" price={total} priceSize={24} nameSize={24} />

      <Box pt={4}>
        {/* <PayPalButton
          getPayPalCheckoutLink={() => getPayPalCheckoutLink(paymentProps)}
          loading={isPaypalLoading}
          isPrivacyTooltip={isDisabledPayButton}
          withTitle
        />

        <Box my={3}>
          <Divider />
        </Box> */}

        <StripeForm
          loading={!loaded}
          appleGooglePayButton={
            <AppleGooglePayButton
              isDisabled={isDisabledPayButton}
              total={total}
              label={packageTitle}
              getClientSecret={() => getStripeClientSecret(paymentProps)}
              onSubmitSuccess={onSubmitSuccess}
            />
          }
        />
      </Box>

      <CheckboxSection />
    </WizardStepWrapper>
  )
}

const withForm = reduxForm<
  Partial<ListingDTO>,
  WizardStepProps & SingletonRouter
>({
  destroyOnUnmount: false,
  shouldAsyncValidate: () => false
})

export default compose<any>(withStripe, withRouter, withForm)(CheckoutStep)
